import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSubscription } from '../utils/api';
import { secureStorage } from '../utils/secureStorage';
import toast from '../utils/toastUtils';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load cached subscription data immediately on mount
  const loadCachedSubscription = () => {
    try {
      const cachedSubscription = secureStorage.getItem('user_subscription');
      if (cachedSubscription) {
        // Check if cached data is not too old (within 30 minutes)
        const cacheTime = secureStorage.getExpiration('user_subscription');
        const now = Date.now();
        const thirtyMinutesAgo = now - (30 * 60 * 1000);
        
        if (cacheTime && cacheTime.getTime() > thirtyMinutesAgo) {
          setSubscription(cachedSubscription);
          setLoading(false);
          return true; // Indicates we found valid cached data
        } else {
          // Cache is expired, remove it
          secureStorage.removeItem('user_subscription');
        }
      }
    } catch (error) {
      console.error('Failed to load cached subscription:', error);
    }
    return false; // No valid cached data found
  };

  const fetchSubscription = async () => {
    try {
      const response = await getSubscription();
      const subscriptionData = response.subscription;
      
      // Cache the subscription data for 30 minutes
      secureStorage.setItem('user_subscription', subscriptionData, 30);
      
      setSubscription(subscriptionData);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      // Only set default free subscription if we don't have cached data
      if (!subscription) {
        setSubscription({
          type: 'Free',
          status: 'Active',
          startDate: new Date(),
          endDate: null
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // First try to load cached data
    const hasCachedData = loadCachedSubscription();
    
    // Always fetch fresh data from server, but don't show loading if we have cached data
    if (hasCachedData) {
      // Fetch in background without showing loading state
      fetchSubscription();
    } else {
      // No cached data, fetch and show loading
      fetchSubscription();
    }
  }, []);

  const isFreeUser = () => {
    return !subscription || subscription.type === 'Free';
  };

  const isExamBasedUser = () => {
    return subscription && (subscription.type === 'TwoExams' || subscription.type === 'FiveExams' || subscription.type === 'CustomExams');
  };

  const isTimeBasedUser = () => {
    return subscription && ['OneWeek', 'TwoWeeks', 'Monthly'].includes(subscription.type) && subscription.status === 'Active';
  };

  const isPremiumUser = () => {
    // Premium is time-based plans
    return isTimeBasedUser();
  };

  const hasExamsRemaining = () => {
    if (!subscription) return false;
    // Time-based plans have unlimited exams
    if (isTimeBasedUser()) return true;
    // Free and exam-based plans check remainingExams
    // For free users, if remainingExams is not set, check hasUsedFreeExam
    if (subscription.type === 'Free') {
      if (subscription.remainingExams !== undefined && subscription.remainingExams !== null) {
        return subscription.remainingExams > 0;
      }
      // If remainingExams is not set, check if free exam has been used
      return !subscription.hasUsedFreeExam;
    }
    // Exam-based plans check remainingExams
    return (subscription.remainingExams !== undefined && subscription.remainingExams !== null) && subscription.remainingExams > 0;
  };

  const isSubscriptionExpired = () => {
    if (!subscription || !subscription.endDate) return false;
    return new Date() > new Date(subscription.endDate);
  };

  const refreshSubscription = async () => {
    setLoading(true);
    // Clear cached data before fetching fresh data
    secureStorage.removeItem('user_subscription');
    await fetchSubscription();
  };

  const clearSubscriptionCache = () => {
    secureStorage.removeItem('user_subscription');
    setSubscription(null);
  };

  const forceRefreshSubscription = async () => {
    setLoading(true);
    clearSubscriptionCache();
    await fetchSubscription();
  };

  const value = {
    subscription,
    loading,
    isFreeUser,
    isExamBasedUser,
    isTimeBasedUser,
    isPremiumUser,
    hasExamsRemaining,
    isSubscriptionExpired,
    refreshSubscription,
    clearSubscriptionCache,
    forceRefreshSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}; 

export { SubscriptionContext };